import React, { useEffect, useMemo, useState } from "react";
import * as Cesium from "cesium";

import { Box, Button, Editable, HStack, Input, SimpleGrid, Spinner, Text, VStack, useDisclosure, Dialog, Portal } from "@chakra-ui/react";
import { Virtuoso } from "react-virtuoso";
import { useAppStore } from "../../../../../context/store";
import {
  LuArrowDownToLine,
  LuCircleDollarSign,
  LuClock,
  LuFocus,
  LuFolderDown,
  LuFolderKanban,
  LuFolderPlus,
  LuFolders,
  LuFolderSync,
  LuHash,
  LuLink,
  LuPen,
  LuPencilRuler,
  LuRefreshCcw,
  LuSaveAll,
  LuText,
  LuTowerControl,
  LuTrash,
  LuUser,
  LuX,
} from "react-icons/lu";
import { serviceCalculateProjectCost, serviceCreateOrEditProjects } from "../../../../../api/services/project";
import { showBBoxOnMap } from "../../../../../utilies/ProjectUtility";
import { deleteAssetsFromAsseest, formatISODateSimple, getCombinedKeysText, removeEpochSuffix, removeFileExtension, showToast } from "../../../../../utilies/Utility";
import { getEditedGeoJSON } from "../../../../../utilies/geojson/GetEditedGeojson";
import Type from "../../../../../utilies/Types";
import { Tooltip } from "../../../../ui/tooltip";
import { serviceGenerateOsm } from "../../../../../api/services/osm";
import { serviceBackup } from "../../../../../api/services/backup";
import { getProjects } from "../../../../../utilies/services/project/GetProject";
import DeleteProjectDialog from "./DeleteProjectDialog";
import ProjectBackupList from "./ProjectBackupList";
import CloseProjectDialog from "./CloseProjectDialog";
import CreateProjectDialog from "./CreateProjectDialog";
import EditProjectDialog from "./EditProjectDialog";
import { useMapObjects } from "../../../../../context/MapObjectsContext";
import { loadProjectLayers } from "../../../../../utilies/loader/project/LoadProject";
import LoadGeneratedOsmDialog from "./LoadGeneratedOsmDialog";
import { selectEntitiesInRectangleOnce } from "../../../../../utilies/other/GetEntitiesInBounds";
import { saveLayerToServerUnified } from "../../../../../utilies/loader/layer/saveLayerToServer";

function Projects() {
  // ===== state =====
  const userData = useAppStore((s) => s.userData);
  const projects = useAppStore((s) => s.projects);
  const selectedProject = useAppStore((s) => s.selectedProject);

  const userType = useAppStore((s) => s.userType);
  const defaultOsmStyle = useAppStore((s) => s.defaultOsmStyle);
  const pointCloudStyle = useAppStore((s) => s.pointCloudStyle);
  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const loadingDataForLayers = useAppStore((s) => s.loadingDataForLayers);
  const loadedPorject = useAppStore((s) => s.loadedPorject);

  // ===== refs =====
  const viewerRef = useAppStore.getState().viewerRef;
  const originalCameraControls = useAppStore.getState().originalCameraControls;
  const savedHotkeysRef = useAppStore.getState().savedHotkeysRef;

  // ===== setters =====
  const setProjects = useAppStore.getState().setProjects;
  const setSelectedProject = useAppStore.getState().setSelectedProject;
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();
  const setImageGeojsonMode = useAppStore.getState().setImageGeojsonMode;

  const setIsShowBackupListDialog = useAppStore.getState().setIsShowBackupListDialog;
  const setSelectedProjectForEdit = useAppStore.getState().setSelectedProjectForEdit;
  const setEditProjectDialog = useAppStore.getState().setEditProjectDialog;

  const setPointCloudStyle = useAppStore.getState().setPointCloudStyle;
  const setImageGeojson = useAppStore.getState().setImageGeojson;
  const setImageGeojsonSelected = useAppStore.getState().setImageGeojsonSelected;
  const setImageDataSource = useAppStore.getState().setImageDataSource;
  const setImageGeojsonCounter = useAppStore.getState().setImageGeojsonCounter;
  const setShowCloseProjectDialog = useAppStore.getState().setShowCloseProjectDialog;
  const setLoadedPorject = useAppStore.getState().setLoadedPorject;
  const setMapObjectsAssest = useAppStore.getState().setMapObjectsAssest;
  const setCreateProjectDialog = useAppStore.getState().setCreateProjectDialog;
  const setIsShowDuplicateProjectDialog = useAppStore.getState().setIsShowDuplicateProjectDialog;
  const setUserType = useAppStore.getState().setUserType;
  const setUserCanEditing = useAppStore.getState().setUserCanEditing;
  const setShowDeleteProjectDialog = useAppStore.getState().setShowDeleteProjectDialog;
  const setLayerTab = useAppStore.getState().setLayerTab;

  const [isProgress, setIsProgress] = useState(false);
  const [isProgressRelodingProjects, setIsProgressRelodingProjects] = useState(false);
  const [backupLoadingProjectId, setBackupLoadingProjectId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [projectCostLoadingId, setProjectCostLoadingId] = useState(null);

  const showLoadGeneratedOsmDialog = useAppStore((s) => s.showLoadGeneratedOsmDialog);
  const setShowLoadGeneratedOsmDialog = useAppStore.getState().setShowLoadGeneratedOsmDialog;

  const [isColorShiftOpen, setIsColorShiftOpen] = useState(false);
  const [pendingLoadArgs, setPendingLoadArgs] = useState(null);
  const [bboxLoadState, setBboxLoadState] = useState({
    open: false,
    drawing: false,
    project: null,
    projectTypeLoading: "main",
    applyColorChange: false,
    bboxRectangle: null,
    questions: [],
    questionIndex: -1,
  });

  const cloneJson = (value) => {
    try {
      return JSON.parse(JSON.stringify(value ?? null));
    } catch {
      return value ?? null;
    }
  };

  const normalizeLayerValue = (raw) => {
    const noExt = removeFileExtension?.(String(raw || "")) ?? String(raw || "");
    const noEpoch = removeEpochSuffix?.(noExt) ?? noExt;

    return String(noEpoch)
      .toLowerCase()
      .replace(/[\s\-_]/g, "");
  };

  const getLayerBaseName = (raw) => {
    const noExt = removeFileExtension?.(String(raw || "")) ?? String(raw || "");
    return removeEpochSuffix?.(noExt) ?? noExt;
  };

  const isRouteLayerName = (raw) => {
    return getLayerBaseName(raw).toUpperCase().startsWith("ROUTE_");
  };

  const isNonGeoJsonProjectLayer = (layer) => {
    const t = String(layer?.layer_type || "").toLowerCase();
    return t === "3d_tiles" || t === "camera" || t === "aerial_imagery";
  };

  const bboxLayerGroups = [
    {
      key: "lanemarker",
      label: "Lanemarker",
      match: (layer) => normalizeLayerValue(layer?.layer_name).includes("lanemarker"),
    },
    {
      key: "virtual_line",
      label: "Virtual line",
      match: (layer) => normalizeLayerValue(layer?.layer_name).includes("virtualline"),
    },
    {
      key: "turning_line",
      label: "Turning line",
      match: (layer) => normalizeLayerValue(layer?.layer_name).includes("turningline"),
    },
    {
      key: "lanelet",
      label: "Lanelet",
      match: (layer) => normalizeLayerValue(layer?.layer_name).includes("lanelet"),
    },
    {
      key: "curbstone",
      label: "Curbstone",
      match: (layer) => normalizeLayerValue(layer?.layer_name).includes("curbstone"),
    },
    {
      key: "road_edge",
      label: "Road edge",
      match: (layer) => normalizeLayerValue(layer?.layer_name).includes("roadedge"),
    },
    {
      key: "intersection",
      label: "Intersection",
      match: (layer) => normalizeLayerValue(layer?.layer_name).includes("intersection"),
    },
    {
      key: "stop_line",
      label: "Stop line",
      match: (layer) => normalizeLayerValue(layer?.layer_name).includes("stopline"),
    },
  ];

  const buildProjectLoadArgs = (item, projectTypeLoading = "main", applyColorChange = false, extra = {}) => ({
    projectTypeLoading,
    project: item,
    viewerRef,
    userData,
    setUserType,
    setUserCanEditing,
    loadingDataForLayers,

    setPointCloudStyle,
    setMapObjects,
    setMapObjectsAssest,

    setImageGeojson,
    setImageGeojsonSelected,
    setImageGeojsonMode,
    setImageDataSource,
    setImageGeojsonCounter,

    originalCameraControls,
    defaultGeojsonStyle,
    basicGeojsonStyle,
    defaultOsmStyle,
    applyColorChange,
    ...extra,
  });

  const buildBBoxQuestions = (project) => {
    const projectLayers = Array.isArray(project?.layers) ? project.layers : [];
    const geojsonLayers = projectLayers.filter((layer) => !isNonGeoJsonProjectLayer(layer));
    const usedIds = new Set();
    const orderedQuestions = [];

    for (const group of bboxLayerGroups) {
      const matchedLayers = geojsonLayers.filter((layer) => group.match(layer));
      if (!matchedLayers.length) continue;

      matchedLayers.forEach((layer) => {
        usedIds.add(layer.id);
      });

      orderedQuestions.push({
        key: group.key,
        label: group.label,
        layerIds: matchedLayers.map((layer) => layer.id),
        layerNames: matchedLayers.map((layer) => layer.layer_name),
      });
    }

    const otherLayers = geojsonLayers.filter((layer) => {
      if (usedIds.has(layer.id)) return false;
      if (isRouteLayerName(layer.layer_name)) return false;
      return true;
    });

    if (otherLayers.length) {
      orderedQuestions.push({
        key: "all_other_map_layers",
        label: "All other map layers",
        layerIds: otherLayers.map((layer) => layer.id),
        layerNames: otherLayers.map((layer) => layer.layer_name),
      });
    }

    const routeLayers = geojsonLayers.filter((layer) => isRouteLayerName(layer.layer_name));
    if (routeLayers.length) {
      orderedQuestions.push({
        key: "all_routes",
        label: "All routes",
        layerIds: routeLayers.map((layer) => layer.id),
        layerNames: routeLayers.map((layer) => layer.layer_name),
      });
    }

    return orderedQuestions;
  };

  const resetBBoxLoadState = () => {
    setBboxLoadState({
      open: false,
      drawing: false,
      project: null,
      projectTypeLoading: "main",
      applyColorChange: false,
      bboxRectangle: null,
      questions: [],
      questionIndex: -1,
    });
  };

  const startLoadWithBBox = ({ project, projectTypeLoading = "main", applyColorChange = false }) => {
    if (!project) return;

    showToast("Please draw a box before loading the map. Press Enter to confirm or ESC to cancel.", Type.NOTIFICATION.INFO, {
      duration: 6000,
    });

    setBboxLoadState({
      open: true,
      drawing: true,
      project,
      projectTypeLoading,
      applyColorChange,
      bboxRectangle: null,
      questions: [],
      questionIndex: -1,
    });
  };

  const confirmBBoxRectangle = ({ project, projectTypeLoading, applyColorChange, bboxRectangle }) => {
    if (!project || !bboxRectangle) {
      resetBBoxLoadState();
      return;
    }

    if (projectTypeLoading === "main") {
      setLayerTab("layers");
      setSelectedProject(project);
    } else {
      setLoadedPorject((prev) => {
        const exists = prev.some((p) => p.id === project.id);
        return exists ? prev : [...prev, project];
      });
    }

    loadProjectLayers(
      buildProjectLoadArgs(project, projectTypeLoading, applyColorChange, {
        onlyNonGeoJsonLayers: true,
      }),
    );

    const questions = buildBBoxQuestions(project);

    if (!questions.length) {
      showToast("No GeoJSON layer is available for Load with BBOX.", Type.NOTIFICATION.INFO);
      resetBBoxLoadState();
      return;
    }

    setBboxLoadState({
      open: true,
      drawing: false,
      project,
      projectTypeLoading,
      applyColorChange,
      bboxRectangle,
      questions,
      questionIndex: 0,
    });
  };

  const answerBBoxQuestion = (shouldLoad) => {
    setBboxLoadState((prev) => {
      const currentQuestion = prev.questions?.[prev.questionIndex];

      if (!prev.project || !prev.bboxRectangle || !currentQuestion) {
        return {
          open: false,
          drawing: false,
          project: null,
          projectTypeLoading: "main",
          applyColorChange: false,
          bboxRectangle: null,
          questions: [],
          questionIndex: -1,
        };
      }

      if (shouldLoad) {
        const layerIds = new Set(currentQuestion.layerIds || []);
        const layerNames = new Set(currentQuestion.layerNames || []);

        loadProjectLayers(
          buildProjectLoadArgs(prev.project, prev.projectTypeLoading, prev.applyColorChange, {
            skipNonGeoJsonLayers: true,
            bboxRectangle: prev.bboxRectangle,
            layerFilter: (layer) => layerIds.has(layer.id) || layerNames.has(layer.layer_name),
          }),
        );
      }

      const nextIndex = prev.questionIndex + 1;
      if (nextIndex >= prev.questions.length) {
        return {
          open: false,
          drawing: false,
          project: null,
          projectTypeLoading: "main",
          applyColorChange: false,
          bboxRectangle: null,
          questions: [],
          questionIndex: -1,
        };
      }

      return {
        ...prev,
        questionIndex: nextIndex,
      };
    });
  };

  const runPendingLoad = (applyColorChange) => {
    if (!pendingLoadArgs) return;

    if (pendingLoadArgs.loadMode === "bbox") {
      startLoadWithBBox({
        project: pendingLoadArgs.project,
        projectTypeLoading: pendingLoadArgs.projectTypeLoading,
        applyColorChange,
      });
    } else {
      loadProjectLayers({
        ...pendingLoadArgs,
        applyColorChange,
      });
    }

    setPendingLoadArgs(null);
    setIsColorShiftOpen(false);
  };

  // ===== derived (perf) =====
  const displayedProjects = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    const q = (searchText ?? "").trim().toLowerCase();

    const filtered = q ? list.filter((p) => (p.project_name ?? "").toLowerCase().includes(q)) : list;

    return [...filtered].sort((a, b) => (b.modified ?? 0) - (a.modified ?? 0));
  }, [projects, searchText]);

  const loadedProjectIds = useMemo(() => {
    return new Set((loadedPorject ?? []).map((p) => p.id));
  }, [loadedPorject]);

  useEffect(() => {
    if (!bboxLoadState.drawing || !viewerRef?.current) return;

    const viewer = viewerRef.current;
    const scene = viewer.scene;
    const controller = scene.screenSpaceCameraController;
    const handler = new Cesium.ScreenSpaceEventHandler(scene.canvas);

    const previousControllerState = {
      enableInputs: controller.enableInputs,
      enableRotate: controller.enableRotate,
      enableTranslate: controller.enableTranslate,
      enableZoom: controller.enableZoom,
      enableTilt: controller.enableTilt,
      enableLook: controller.enableLook,
    };

    controller.enableInputs = false;
    controller.enableRotate = false;
    controller.enableTranslate = false;
    controller.enableZoom = false;
    controller.enableTilt = false;
    controller.enableLook = false;

    let isDragging = false;
    let startCartographic = null;
    let currentCartographic = null;
    let finalRectangle = null;
    let previewRectangle = null;

    const pickCartographic = (position) => {
      if (!position) return null;

      let cartesian = null;

      if (scene.pickPositionSupported) {
        try {
          cartesian = scene.pickPosition(position);
        } catch {}
      }

      if (!cartesian) {
        const ray = scene.camera.getPickRay(position);
        if (ray) {
          cartesian = scene.globe.pick(ray, scene);
        }
      }

      if (!cartesian) {
        cartesian = scene.camera.pickEllipsoid(position, scene.globe.ellipsoid);
      }

      return cartesian ? Cesium.Cartographic.fromCartesian(cartesian) : null;
    };

    const buildRectangle = (a, b) => {
      return new Cesium.Rectangle(
        Math.min(a.longitude, b.longitude),
        Math.min(a.latitude, b.latitude),
        Math.max(a.longitude, b.longitude),
        Math.max(a.latitude, b.latitude),
      );
    };

    const getCurrentRectangle = () => {
      if (!startCartographic || !currentCartographic) return null;

      const rectangle = buildRectangle(startCartographic, currentCartographic);
      const width = Math.abs(rectangle.east - rectangle.west);
      const height = Math.abs(rectangle.north - rectangle.south);

      if (width <= Cesium.Math.EPSILON7 || height <= Cesium.Math.EPSILON7) {
        return null;
      }

      return rectangle;
    };

    const ensurePreview = () => {
      if (previewRectangle) return;

      previewRectangle = viewer.entities.add({
        rectangle: {
          coordinates: new Cesium.CallbackProperty(() => {
            return finalRectangle || getCurrentRectangle() || Cesium.Rectangle.fromDegrees(0, 0, 0, 0);
          }, false),
          material: Cesium.Color.CYAN.withAlpha(0.12),
          outline: true,
          outlineColor: Cesium.Color.CYAN,
        },
      });
    };

    const removePreview = () => {
      if (previewRectangle) {
        viewer.entities.remove(previewRectangle);
        previewRectangle = null;
      }
    };

    handler.setInputAction((click) => {
      const cartographic = pickCartographic(click.position);
      if (!cartographic) return;

      isDragging = true;
      startCartographic = cartographic;
      currentCartographic = cartographic;
      finalRectangle = null;

      removePreview();
      ensurePreview();
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    handler.setInputAction((movement) => {
      if (!isDragging || !startCartographic) return;

      const cartographic = pickCartographic(movement.endPosition);
      if (!cartographic) return;

      currentCartographic = cartographic;
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction((release) => {
      if (!isDragging) return;

      isDragging = false;

      const cartographic = pickCartographic(release.position);
      if (cartographic) {
        currentCartographic = cartographic;
      }

      finalRectangle = getCurrentRectangle();

      if (!finalRectangle) {
        removePreview();
        startCartographic = null;
        currentCartographic = null;
      }
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        removePreview();
        resetBBoxLoadState();
        showToast("Load with BBOX cancelled.", Type.NOTIFICATION.WARNING);
        return;
      }

      if (event.key !== "Enter") return;

      event.preventDefault();

      const rectangle = finalRectangle || getCurrentRectangle();

      if (!rectangle) {
        showToast("Please draw a box before loading the map.", Type.NOTIFICATION.WARNING);
        return;
      }

      removePreview();

      confirmBBoxRectangle({
        project: bboxLoadState.project,
        projectTypeLoading: bboxLoadState.projectTypeLoading,
        applyColorChange: bboxLoadState.applyColorChange,
        bboxRectangle: rectangle,
      });
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      removePreview();
      handler.destroy();

      controller.enableInputs = previousControllerState.enableInputs;
      controller.enableRotate = previousControllerState.enableRotate;
      controller.enableTranslate = previousControllerState.enableTranslate;
      controller.enableZoom = previousControllerState.enableZoom;
      controller.enableTilt = previousControllerState.enableTilt;
      controller.enableLook = previousControllerState.enableLook;
    };
  }, [
    bboxLoadState.drawing,
    bboxLoadState.project,
    bboxLoadState.projectTypeLoading,
    bboxLoadState.applyColorChange,
    viewerRef,
  ]);

  useEffect(() => {
    if (!bboxLoadState.open || bboxLoadState.drawing || bboxLoadState.questionIndex < 0) return;

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        answerBBoxQuestion(true);
      } else if (event.key === "Escape") {
        event.preventDefault();
        answerBBoxQuestion(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [bboxLoadState.open, bboxLoadState.drawing, bboxLoadState.questionIndex, bboxLoadState.questions]);

  // Keyboard events
  useEffect(() => {
    const isMod = (k) => ["shift", "ctrl", "alt", "meta"].includes(String(k).toLowerCase());
    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));
    const normalizeKey = (k) => {
      let key = String(k).toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      return key;
    };

    const getCombo = (type) => savedHotkeysRef.current.find((h) => h.type === type)?.combination || [];

    const matches = (e, combo) => {
      if (!combo || combo.length === 0) return false;

      const norm = combo.map(normalizeKey);

      const want = {
        shift: norm.includes("shift"),
        ctrl: norm.includes("ctrl"),
        alt: norm.includes("alt"),
        meta: norm.includes("meta"),
      };

      if (e.shiftKey !== want.shift) return false;
      if (e.ctrlKey !== want.ctrl) return false;
      if (e.altKey !== want.alt) return false;
      if (e.metaKey !== want.meta) return false;

      const main = norm.find((k) => !isMod(k));
      if (!main) return true;

      const pressed = normalizeKey(e.key);
      return pressed === main;
    };

    // -------------------------
    // 3-key sequential support
    // combo: [mod, letter1, letter2]
    // user: mod+letter1, then within 600ms press letter2 (with same mods or no mods)
    // -------------------------
    const PENDING_KEY = "__save_project_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = {
        active: false,
        mods: [],
        stepKey: "",
        finalKey: "",
        expiresAt: 0,
        tid: null,
      };
    }
    const pending = window[PENDING_KEY];

    const resetPending = () => {
      if (pending.tid) clearTimeout(pending.tid);
      pending.active = false;
      pending.mods = [];
      pending.stepKey = "";
      pending.finalKey = "";
      pending.expiresAt = 0;
      pending.tid = null;
    };

    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const isSequential3Combo = (combo) => {
      if (!combo || combo.length !== 3) return false;

      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));

      return mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);
    };

    const getSequentialParts = (combo) => {
      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));
      return { mods, stepKey: nonMods[0], finalKey: nonMods[1] };
    };

    const startPending = (mods, stepKey, finalKey) => {
      resetPending();
      pending.active = true;
      pending.mods = mods;
      pending.stepKey = stepKey;
      pending.finalKey = finalKey;
      pending.expiresAt = Date.now() + 3000;
      pending.tid = setTimeout(() => resetPending(), 3000);
    };

    const handleKeyDown = (event) => {
      const combo = getCombo(Type.HOTKEYS.SAVE_PROJECT);

      // 1) pending final key check
      if (pending.active) {
        if (Date.now() > pending.expiresAt) {
          resetPending();
        } else {
          const k = normalizeKey(event.key);
          const okMods = modsMatchExactly(event, pending.mods) || modsAreNone(event);

          if (okMods && k === normalizeKey(pending.finalKey)) {
            event.preventDefault();
            resetPending();

            if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return;

            if (selectedProject) {
              saveAllLayers(selectedProject);
            }
            return;
          }

          if (!isMod(k)) resetPending();
        }
      }

      // 2) sequential start: mods + first letter
      if (isSequential3Combo(combo)) {
        const { mods, stepKey, finalKey } = getSequentialParts(combo);

        if (modsMatchExactly(event, mods) && normalizeKey(event.key) === normalizeKey(stepKey)) {
          event.preventDefault();
          startPending(mods, stepKey, finalKey);
          return;
        }
      }

      // 3) normal one-shot behavior
      if (matches(event, combo)) {
        event.preventDefault();
        resetPending();

        if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return;

        if (selectedProject) {
          saveAllLayers(selectedProject);
        }
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      resetPending();
    };
  }, [viewerRef, selectedProject, mapObjects]);

  const renameProject = (newProjectName, projectId) => {
    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        username: userData.user.username,
        project_name: newProjectName,
      }),
    );
    formData.append("project_id", projectId);

    serviceCreateOrEditProjects(formData, userData.token)
      .then((res) => {
        if (res.data.status == "success") {
          showToast("Project name changed successfully", Type.NOTIFICATION.SUCCESS);
          getProjects({
            userData,
            setProjects,
          });
        }
      })
      .catch((err) => {
        showToast(err.message, Type.NOTIFICATION.WARNING);
        console.error("Error", err);
      });
  };

  const changeDescProject = (newProjectDesc, projectId) => {
    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        username: userData.user.username,
        description: newProjectDesc,
      }),
    );
    formData.append("project_id", projectId);

    serviceCreateOrEditProjects(formData, userData.token)
      .then((res) => {
        if (res.data.status == "success") {
          showToast("Project description changed successfully", Type.NOTIFICATION.SUCCESS);
          getProjects({
            userData,
            setProjects,
          });
        }
      })
      .catch((err) => {
        showToast(err.message, Type.NOTIFICATION.WARNING);
        console.error("Error", err);
      });
  };

  const createFeatureMergeKey = (feature, index = 0) => {
    const rawId =
      feature?.id ??
      feature?.properties?.id ??
      feature?.properties?.ID ??
      feature?.properties?.fid ??
      feature?.properties?.FID ??
      feature?.properties?.objectid ??
      feature?.properties?.OBJECTID;

    if (rawId !== undefined && rawId !== null && rawId !== "") {
      return `id:${rawId}`;
    }

    return `geom:${JSON.stringify(feature?.geometry ?? null)}:${index}`;
  };

  const mergeHiddenAndVisibleGeojson = (layer) => {
    const editedVisibleGeojson = getEditedGeoJSON(layer);
    const hiddenFeatures = Array.isArray(layer?.hiddenGeojson?.features) ? layer.hiddenGeojson.features : [];
    const visibleFeatures = Array.isArray(editedVisibleGeojson?.features) ? editedVisibleGeojson.features : [];
    const mergedFeatures = new Map();

    hiddenFeatures.forEach((feature, index) => {
      mergedFeatures.set(createFeatureMergeKey(feature, index), cloneJson(feature));
    });

    visibleFeatures.forEach((feature, index) => {
      mergedFeatures.set(createFeatureMergeKey(feature, index), cloneJson(feature));
    });

    return {
      type: "FeatureCollection",
      features: Array.from(mergedFeatures.values()),
    };
  };

  const buildLayerForSave = (layer) => {
    if (!layer?.hiddenGeojson) return layer;

    const mergedGeojson = mergeHiddenAndVisibleGeojson(layer);

    return {
      ...layer,
      original_geojson: mergedGeojson,
      geojsonData: mergedGeojson,
      geojson: mergedGeojson,
      data: mergedGeojson,
      full_geojson: mergedGeojson,
      __merged_geojson_for_save: mergedGeojson,
    };
  };

  const saveAllLayers = async (item) => {
    const projectId = item.id;

    let listOfLayersId = [];
    let tempMapObjects = [...mapObjects];

    for (const key in mapObjects) {
      if (mapObjects[key]?.layer_id) {
        listOfLayersId.push(mapObjects[key]?.layer_id);
      }
    }

    for (const key in mapObjects) {
      if (mapObjects[key].layer_id && !mapObjects[key].is_edited && mapObjects[key].project_id) continue;
      setIsProgress(true);

      const layerToSave = buildLayerForSave(mapObjects[key]);

      await saveLayerToServerUnified({
        layer: layerToSave,
        userData,
        pointCloudStyle,
        description: "",
        createFileBaseName: removeEpochSuffix(removeFileExtension(layerToSave.file_name)),
      })
        .then((res) => {
          if (res.data.status == "failed") {
            showToast(res.data.message, Type.NOTIFICATION.WARNING);
          } else {
            showToast("Stored " + removeEpochSuffix(removeFileExtension(res.data.layer_name)), Type.NOTIFICATION.SUCCESS);
            listOfLayersId.push(res.data.layer_id);
            tempMapObjects[key].file_name = removeFileExtension(res.data.layer_name) + (mapObjects[key].type != Type.ASSET.IMAGERY ? mapObjects[key].file_type : "");
            tempMapObjects[key].is_edited = false;
            tempMapObjects[key].project_id = projectId;
            tempMapObjects[key].layer_id = res.data.layer_id;
            tempMapObjects[key].original_geojson = layerToSave.__merged_geojson_for_save || getEditedGeoJSON(mapObjects[key]);
            tempMapObjects[key].hiddenGeojson = layerToSave.__merged_geojson_for_save
              ? {
                  type: "FeatureCollection",
                  features: [],
                }
              : tempMapObjects[key].hiddenGeojson;
          } 
        })
        .catch((err) => {
          console.error("Error", err);
          setIsProgress(false);
        });
    }

    setMapObjects(() => tempMapObjects);

    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        username: userData.user.username,
        layers: [...new Set(listOfLayersId)],
      }),
    );
    formData.append("project_id", projectId);

    serviceCreateOrEditProjects(formData, userData.token)
      .then((res) => {
        if (res.data.status == "success") {
          showToast(res.data.message, Type.NOTIFICATION.SUCCESS);
          getProjects({
            userData,
            setProjects,
            onSuccess: () => {
              setIsProgress(false);
            },
            onError: () => {
              setIsProgress(false);
            },
          });
        }
      })
      .catch((err) => {
        showToast(err.message, Type.NOTIFICATION.WARNING);
        console.error("Error", err);
      });
  };

  const isShowOsmGenerateButton = (item) => {
    if (!item?.layers || !Array.isArray(item.layers)) return false;

    const requiredLayers = ["lane_marker", "virtual_line", "lanelet"];
    const layerNames = item.layers.map((l) => l.layer_name?.toLowerCase());

    const requiredOnly = layerNames.filter((name) => requiredLayers.some((req) => name.startsWith(req)));

    const hasAllRequired = requiredLayers.every((req) => requiredOnly.some((name) => name.startsWith(req)));
    if (!hasAllRequired) return false;

    const cleanedRequired = requiredOnly.map((name) => removeFileExtension(removeEpochSuffix(name)));
    const uniqueRequired = new Set(cleanedRequired);
    const noDuplicates = cleanedRequired.length === uniqueRequired.size;

    return noDuplicates;
  };

  const generateOsmFile = (item) => {
    serviceGenerateOsm({
      project_id: item.id,
    })
      .then((res) => {
        if (res.data.status == "failed") {
        } else {
          setShowLoadGeneratedOsmDialog(res.data.url);
        }
      })
      .catch((err) => {
        console.error("Error", err);
      });
  };

  const backup = (item) => {
    setBackupLoadingProjectId(item.id);

    const formData = new FormData();
    formData.append("username", userData.user.username);
    formData.append("project_id", item.id);
    formData.append("action", "create");

    serviceBackup(formData)
      .then((res) => {
        if (res.data.status == "success") {
          showToast(res.data.message, Type.NOTIFICATION.SUCCESS);
        } else {
          showToast(res.data.message, Type.NOTIFICATION.WARNING);
        }
      })
      .catch((err) => {
        console.error("Error", err);
        showToast(err.message, Type.NOTIFICATION.ERROR);
      })
      .finally(() => {
        setBackupLoadingProjectId(null);
      });
  };

  const downloadFile = (url) => {
    try {
      if (!url) {
        showToast("Invalid URL", Type.NOTIFICATION.WARNING);
        return;
      }

      const fileName = url.split("/").pop().split("?")[0] || "downloaded_file";

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.target = "_blank";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast(`Downloading ${fileName}`, Type.NOTIFICATION.SUCCESS);
    } catch (err) {
      console.error("Error downloading file:", err);
      showToast(err.message || "Download failed", Type.NOTIFICATION.ERROR);
    }
  };

  const getPreviewText = (text) => {
    if (!text) return "";
    const trimmed = text.trim();
    if (!trimmed) return "";
    const words = trimmed.split(/\s+/);
    if (words.length <= 10) return trimmed;
    return words.slice(0, 10).join(" ") + " ...";
  };

  const calculateProjectCost = (project) => {
    const projectId = project?.id;
    if (!projectId) return;

    setProjectCostLoadingId(projectId);

    const formData = new FormData();
    formData.append("project_id", projectId);

    serviceCalculateProjectCost(formData, userData.token)
      .then((res) => {
        if (res.data.status == "success") {
          showToast(`Project cost is $${res.data.total_cost}`, Type.NOTIFICATION.SUCCESS, { duration: 5000 });
        } else {
          showToast(res.data.message, Type.NOTIFICATION.WARNING);
        }
      })
      .catch((err) => {
        showToast(err.message, Type.NOTIFICATION.WARNING);
        console.error("Error", err);
      })
      .finally(() => {
        setProjectCostLoadingId(null);
      });
  };

  return (
    <>
      <SimpleGrid columns={7} gap={1} my={2} mb={0} width="full">
        {userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN) ? (
          <Button
            variant="outline"
            size={"2xl"}
            p={0}
            height={"65px"}
            onClick={() => {
              setCreateProjectDialog(true);
            }}
          >
            <Tooltip content={"Create New Project"}>
              <LuFolderPlus size={20} />
            </Tooltip>
          </Button>
        ) : null}
        <Button
          variant="outline"
          size={"2xl"}
          p={0}
          height={"65px"}
          onClick={() => {
            setIsProgressRelodingProjects(true);
            getProjects({
              userData,
              setProjects,
              onSuccess: () => {
                setIsProgressRelodingProjects(false);
              },
              onError: () => {
                setIsProgressRelodingProjects(false);
              },
            });
          }}
        >
          <Tooltip content={"Reload Projects"}>
            {isProgressRelodingProjects ? (
              <VStack gap={0}>
                <Spinner size={"sm"} />
              </VStack>
            ) : (
              <LuRefreshCcw size={20} />
            )}
          </Tooltip>
        </Button>
      </SimpleGrid>

      <Box flex="1" minH={0} overflow="hidden">
        <Box display="flex" flexDir="column" h="100%">
          <Input
            placeholder="Type to search in projects..."
            size="sm"
            height="30px"
            width="full"
            my={2}
            onChange={(v) => {
              setSearchText(v.target.value);
            }}
          />

          <Box borderWidth="1px" borderRadius="md" flex="1" minH={0} overflow="hidden">
            {projects?.length === 0 ? (
              <Text p={3}>There is no project</Text>
            ) : (
              <Virtuoso
                style={{ height: "100%" }}
                data={displayedProjects}
                computeItemKey={(index, item) => item?.id ?? index}
                itemContent={(index, item) => {
                  const isLoaded = loadedProjectIds.has(item.id);
                  const isSelected = selectedProject?.id == item?.id;

                  return (
                    <Box position="relative" borderBottomWidth="1px" p={3} pt={0} bg={isSelected ? "green.200" : "transparent"}>
                      <HStack justify={"right"} pt={3}>
                        {(userType.includes(Type.ROLE.SUPER_ADMIN) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.DIGITIZER)) && (
                          <Button
                            disabled={projectCostLoadingId === item.id}
                            display={"contents"}
                            mx={0}
                            color={"MenuText"}
                            p={0}
                            onClick={() => {
                              calculateProjectCost(item);
                            }}
                          >
                            <Tooltip content="Project Cost">{projectCostLoadingId === item.id ? <Spinner size="sm" /> : <LuCircleDollarSign />}</Tooltip>
                          </Button>
                        )}
                        {!(isLoaded || isSelected) && userType.includes(Type.ROLE.SUPER_ADMIN) && (
                          <Button
                            display={"contents"}
                            mx={0}
                            bg={"transparent"}
                            outline={"none"}
                            color={"red"}
                            p={0}
                            onClick={() => {
                              setShowDeleteProjectDialog({
                                open: true,
                                data: item,
                              });
                            }}
                          >
                            <Tooltip content="Delete Project">
                              <LuTrash />
                            </Tooltip>
                          </Button>
                        )}

                        {item.ground_mesh_url && (
                          <Button
                            display={"contents"}
                            mx={0}
                            bg={"transparent"}
                            outline={"none"}
                            color={"MenuText"}
                            p={0}
                            onClick={() => {
                              downloadFile(item.ground_mesh_url);
                            }}
                          >
                            <Tooltip content="Download  Graound Mesh">
                              <LuArrowDownToLine />
                            </Tooltip>
                          </Button>
                        )}
                        {item.pcd_tiles_url && (
                          <Button
                            display={"contents"}
                            mx={0}
                            bg={"transparent"}
                            outline={"none"}
                            color={"MenuText"}
                            p={0}
                            onClick={() => {
                              downloadFile(item.pcd_tiles_url);
                            }}
                          >
                            <Tooltip content="Download PCD Tiles">
                              <LuArrowDownToLine />
                            </Tooltip>
                          </Button>
                        )}
                        {item.routes_url && (
                          <Button
                            display={"contents"}
                            mx={0}
                            bg={"transparent"}
                            outline={"none"}
                            color={"MenuText"}
                            p={0}
                            onClick={() => {
                              downloadFile(item.routes_url);
                            }}
                          >
                            <Tooltip content="Download Routes">
                              <LuArrowDownToLine />
                            </Tooltip>
                          </Button>
                        )}
                        {item.semantic_map_url && (
                          <Button
                            display={"contents"}
                            mx={0}
                            bg={"transparent"}
                            outline={"none"}
                            color={"MenuText"}
                            p={0}
                            onClick={() => {
                              downloadFile(item.semantic_map_url);
                            }}
                          >
                            <Tooltip content="Download Semantic Map">
                              <LuArrowDownToLine />
                            </Tooltip>
                          </Button>
                        )}

                        {userType.includes(Type.ROLE.SUPER_ADMIN) ? (
                          isSelected ? (
                            <Button
                              display={"contents"}
                              mx={0}
                              bg={"transparent"}
                              outline={"none"}
                              color={"MenuText"}
                              p={0}
                              onClick={() => {
                                setCreateProjectDialog(true);
                                setIsShowDuplicateProjectDialog({
                                  open: true,
                                  project: item,
                                });
                              }}
                            >
                              <Tooltip content="Duplicate">
                                <LuFolders />
                              </Tooltip>
                            </Button>
                          ) : null
                        ) : null}

                        <Button
                          display={"contents"}
                          mx={0}
                          bg={"transparent"}
                          outline={"none"}
                          color={"MenuText"}
                          p={0}
                          onClick={() => {
                            setIsShowBackupListDialog({
                              open: true,
                              project_id: item.id,
                            });
                          }}
                        >
                          <Tooltip content="Backup List">
                            <LuFolderKanban />
                          </Tooltip>
                        </Button>

                        {userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN) ? (
                          <Button
                            bg={"transparent"}
                            display={"contents"}
                            outline={"none"}
                            color={"MenuText"}
                            p={0}
                            onClick={() => {
                              backup(item);
                            }}
                          >
                            <Tooltip content="Create Backup">{backupLoadingProjectId === item.id ? <Spinner size={"sm"} /> : <LuFolderSync />}</Tooltip>
                          </Button>
                        ) : null}

                        {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                          isSelected && (
                            <Button
                              display={"contents"}
                              bg={"transparent"}
                              outline={"none"}
                              color={"MenuText"}
                              p={0}
                              onClick={() => {
                                if (isSelected) {
                                  saveAllLayers(item);
                                }
                              }}
                            >
                              <Tooltip content={"Save " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SAVE_PROJECT)}>
                                {isProgress && isSelected ? (
                                  <VStack gap={0}>
                                    <Spinner size={"sm"} />
                                  </VStack>
                                ) : (
                                  <LuSaveAll />
                                )}
                              </Tooltip>
                            </Button>
                          )}

                        {isSelected && isShowOsmGenerateButton(item) && (
                          <Button
                            display={"contents"}
                            bg={"transparent"}
                            outline={"none"}
                            color={"MenuText"}
                            p={0}
                            onClick={() => {
                              generateOsmFile(item);
                            }}
                          >
                            <Tooltip content="Generate OSM file">
                              <LuPencilRuler />
                            </Tooltip>
                          </Button>
                        )}

                        <Button
                          display={"contents"}
                          bg={"transparent"}
                          outline={"none"}
                          color={"MenuText"}
                          p={0}
                          onClick={() => {
                            const baseUrl = window.location.origin;
                            const url = `${baseUrl}/?project_id=${item.id}`;

                            navigator.clipboard
                              .writeText(url)
                              .then(() => {
                                showToast("Copied to clipboard", Type.NOTIFICATION.SUCCESS);
                              })
                              .catch((err) => {
                                showToast(err.message, Type.NOTIFICATION.ERROR);
                              });
                          }}
                        >
                          <Tooltip content={"Copy Project Link"}>
                            <LuLink />
                          </Tooltip>
                        </Button>

                        <Button
                          display={"contents"}
                          bg={"transparent"}
                          outline={"none"}
                          color={"MenuText"}
                          p={0}
                          onClick={() => {
                            showBBoxOnMap(viewerRef.current, item.bbox);
                          }}
                        >
                          <Tooltip content={"Focus to Project Bounding Box"}>
                            <LuFocus />
                          </Tooltip>
                        </Button>

                        {userType.includes(Type.ROLE.SUPER_ADMIN) ? (
                          <Button
                            display={"contents"}
                            bg={"transparent"}
                            outline={"none"}
                            color={"MenuText"}
                            p={0}
                            onClick={() => {
                              setEditProjectDialog(true);
                              setSelectedProjectForEdit(item);
                            }}
                          >
                            <Tooltip content={"Edit Project"}>
                              <LuPen />
                            </Tooltip>
                          </Button>
                        ) : null}

                        {!(isLoaded || isSelected) && (
                          <Button
                            display={"contents"}
                            bg={"transparent"}
                            outline={"none"}
                            color={"MenuText"}
                            p={0}
                            onClick={() => {
                              if (selectedProject == null) {
                                startLoadWithBBox({
                                  project: item,
                                  projectTypeLoading: "main",
                                  applyColorChange: false,
                                });
                              } else {
                                setPendingLoadArgs({
                                  ...buildProjectLoadArgs(item, "assest"),
                                  loadMode: "bbox",
                                });
                                setIsColorShiftOpen(true);
                              }
                            }}
                          >
                            <Tooltip content={"Load with BBOX"}>
                              <LuFocus />
                            </Tooltip>
                          </Button>
                        )}

                        <Button
                          display={"contents"}
                          bg={"transparent"}
                          outline={"none"}
                          color={"MenuText"}
                          p={0}
                          onClick={() => {
                            if (selectedProject == null && !isLoaded) {
                              setLayerTab("layers");
                              setSelectedProject(item);
                              loadProjectLayers(buildProjectLoadArgs(item, "main"));
                            } else if (isSelected) {
                              setShowCloseProjectDialog(true);
                            } else {
                              if (!isLoaded) {
                                setLoadedPorject((prev) => {
                                  const exists = prev.some((p) => p.id === item.id);
                                  return exists ? prev : [...prev, item];
                                });

                                setPendingLoadArgs({
                                  ...buildProjectLoadArgs(item, "assest"),
                                  loadMode: "normal",
                                });

                                setIsColorShiftOpen(true);
                              } else {
                                deleteAssetsFromAsseest(viewerRef.current, item, setMapObjectsAssest, setLoadedPorject, false);
                              }
                            }
                          }}
                        >
                          <Tooltip content={isLoaded || isSelected ? "Close Project" : "Open Project"}>{isLoaded || isSelected ? <LuX /> : <LuFolderDown />}</Tooltip>
                        </Button>
                      </HStack>

                      <HStack>
                        <LuText />
                        <Editable.Root
                          value={item.project_name}
                          activationMode="dblclick"
                          width={"fit-content"}
                          disabled={
                            !userType.includes(Type.ROLE.ANNOTATOR) && !userType.includes(Type.ROLE.DIGITIZER) && !userType.includes(Type.ROLE.ADMIN) && !userType.includes(Type.ROLE.SUPER_ADMIN)
                          }
                        >
                          <Editable.Preview />

                          <Editable.Input
                            data-allow-hotkeys="Enter"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                e.currentTarget.blur();
                              }
                            }}
                            onBlur={(e) => {
                              const newVal = (e.target.value ?? "").trim();
                              const oldVal = (item.project_name ?? "").trim();

                              if (newVal !== oldVal) {
                                renameProject(newVal, item.id);
                              }
                            }}
                          />
                        </Editable.Root>
                        <Text fontSize={"12px"}>(Layers Count: {item.layers?.length})</Text>
                      </HStack>

                      <HStack>
                        <LuText />
                        <Editable.Root
                          value={item.description || ""}
                          activationMode="dblclick"
                          width={"80%"}
                          disabled={![Type.ROLE.ANNOTATOR, Type.ROLE.ADMIN, Type.ROLE.SUPER_ADMIN, Type.ROLE.DIGITIZER].some((r) => userType.includes(r))}
                        >
                          <Editable.Preview
                            style={{
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              textAlign: "start",
                            }}
                          >
                            {getPreviewText(item.description || "")}
                          </Editable.Preview>

                          <Editable.Input
                            data-allow-hotkeys="Enter"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                e.currentTarget.blur();
                              }
                            }}
                            onBlur={(e) => {
                              const newVal = e.target.value || "";
                              const original = item.description || "";
                              if (newVal !== original) changeDescProject(newVal, item.id);
                            }}
                          />
                        </Editable.Root>
                      </HStack>

                      {item?.city ? (
                        <HStack>
                          <LuTowerControl />
                          <Text>{item.city}</Text>
                        </HStack>
                      ) : null}
                      {item?.project_code ? (
                        <HStack>
                          <LuHash />
                          <Text>{item.project_code}</Text>
                        </HStack>
                      ) : null}

                      <HStack>
                        <LuUser />
                        <Text>
                          {item.username}
                          {item.contributors?.find((c) => c.username === item.username)?.role && ` (${item.contributors.find((c) => c.username === item.username).role})`}
                        </Text>
                      </HStack>

                      <HStack>
                        <LuClock />
                        Start Date: <Text>{formatISODateSimple(item.timestamp)}</Text>
                      </HStack>
                      <HStack>
                        <LuClock />
                        Last Edit: <Text>{formatISODateSimple(item.modified)}</Text>
                      </HStack>
                    </Box>
                  );
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <CloseProjectDialog saveAllLayers={saveAllLayers} />
      <DeleteProjectDialog />
      <ProjectBackupList />
      <CreateProjectDialog />
      <EditProjectDialog />
      <LoadGeneratedOsmDialog />

      <Dialog.Root
        open={bboxLoadState.open && !bboxLoadState.drawing && bboxLoadState.questionIndex >= 0}
        onOpenChange={(e) => {
          if (!e.open) {
            resetBBoxLoadState();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Load with BBOX</Dialog.Header>
              <Dialog.Body>
                <Text>Please confirm loading this layer group:</Text>
                <Text mt={2} fontWeight={"bold"}>
                  {bboxLoadState.questions?.[bboxLoadState.questionIndex]?.label || ""}
                </Text>
                <Text mt={2}>Press Enter to load or ESC to skip.</Text>
              </Dialog.Body>
              <Dialog.Footer style={{ display: "flex", gap: 8 }}>
                <Button variant="outline" onClick={() => answerBBoxQuestion(false)}>
                  Skip
                </Button>
                <Button onClick={() => answerBBoxQuestion(true)}>Load</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={isColorShiftOpen}
        onOpenChange={(e) => {
          const nextOpen = !!e.open;
          if (!nextOpen) runPendingLoad(false);
          else setIsColorShiftOpen(true);
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Apply color change?</Dialog.Header>
              <Dialog.Body>
                <Text>Do you want to apply color change?</Text>
              </Dialog.Body>
              <Dialog.Footer style={{ display: "flex", gap: 8 }}>
                <Button variant="outline" onClick={() => runPendingLoad(false)}>
                  No
                </Button>
                <Button onClick={() => runPendingLoad(true)}>Yes</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}

export default Projects;
